/*
 * File: api.js
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:54 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 1, 2025 at 1:37 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import axios from 'axios';

// Utilities
import { getCookie } from '../utilities/cookies';

// Constants
import {
	CLIENT_PLATFORM_HEADER, CLIENT_VERSION_HEADER, COUNTRY_COOKIE, EMBED_COOKIE, LOCALE_COOKIE, PARSE_APP_ID_HEADER, PARSE_SESSION_TOKEN_HEADER, SESSION_TOKEN_COOKIE
} from '../../constants/base.constants';


/**
 * API Handlers
 */

export const api = axios.create({
	baseURL: `${process.env.API_URL}`,
	withCredentials: true,
});


/**
 * Configure Internal API
 */

api.interceptors.request.use(async (config) => {

	// Get session token
	const sessionToken = getCookie(SESSION_TOKEN_COOKIE); // NV - IN STORE AS WELL

	// Get locale id
	const localeId = getCookie(LOCALE_COOKIE); // NV - IN STORE AS WELL

	// Get country id
	const countryId = getCookie(COUNTRY_COOKIE); // NV - IN STORE AS WELL

	// Get embed id
	const embedId = getCookie(EMBED_COOKIE); // NV - IN STORE AS WELL - NEED TO SET THIS SOMEWHERE

	// Get headers
	const headers = {
		...config.headers,
		[PARSE_APP_ID_HEADER]: process.env.APP_ID,
		[CLIENT_VERSION_HEADER]: process.env.PACKAGE_VERSION,
		[CLIENT_PLATFORM_HEADER]: 'web',
		...sessionToken ? {
			[PARSE_SESSION_TOKEN_HEADER]: sessionToken
		} : undefined
	};

	// Add parameters to data
	const data = config.data instanceof FormData ? null : {
		...(config.data || {}),
		...embedId ? {
			embed_id: embedId
		} : undefined,
		...localeId ? {
			locale_id: localeId
		} : undefined,
		...countryId ? {
			country_id: countryId
		} : undefined
	};

	// Add query parameters if GET
	let { params } = config;
	if (config.method === 'get') {
		params = {
			...(config.params || {}),
			...embedId ? {
				embed_id: embedId
			} : undefined,
			...localeId ? {
				locale_id: localeId
			} : undefined,
			...countryId ? {
				country_id: countryId
			} : undefined
		};
	}

	// Return headers
	return {
		...config,
		headers,
		...data ? {
			data
		} : undefined,
		params
	};
});

api.interceptors.response.use((response) => response, (error) => {
	if (error && error.response
		&& (error.response.status === 209 || error.response.data?.code === 209 || error.response.data?.error?.code === 209)) {

		// Redirect to logout
		window.location = '/logout';
	}
	return Promise.reject(error);
});
